import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthenticatedFetch } from '../../utils/api';

/**
 * CreditsWidget — compact credit balance panel using Shopify Polaris s-* components.
 *
 * States:
 *  Loading  — shimmer skeleton
 *  Healthy  — green bar, success badge
 *  Low ≤20% — amber bar, attention badge, "Top up" primary button
 *  Empty    — red bar, critical badge, "Buy credits" primary button
 *
 * Props:
 *  refreshKey     — increment to force an immediate re-fetch
 *  onPurchase     — called when user clicks the action button
 *  compact        — smaller padding when true (default)
 *  pollIntervalMs — refresh interval in ms (default 20 000; 0 = off)
 */
function CreditsWidget({ refreshKey = 0, onPurchase, compact = true, pollIntervalMs = 20000 }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const authFetchRef = useRef(authenticatedFetch);
  useEffect(() => { authFetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchBalance = useCallback(async () => {
    const doFetch = authFetchRef.current;
    if (!doFetch) return;
    try {
      const res  = await doFetch('/api/credits/balance');
      const json = await res.json();
      if (json.success) setBalance(json.data);
    } catch (_) { /* non-critical, fail silently */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance, refreshKey]);

  useEffect(() => {
    if (!pollIntervalMs || pollIntervalMs <= 0) return;
    intervalRef.current = setInterval(fetchBalance, pollIntervalMs);
    return () => clearInterval(intervalRef.current);
  }, [fetchBalance, pollIntervalMs]);

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <s-box padding={compact ? 'small' : 'base'} border="base" borderRadius="base" background="subdued">
        <s-stack gap="small-200">
          <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
            <div className="shimmer-loader" style={{ height: 22, width: 100, borderRadius: 6 }} />
            <div className="shimmer-loader" style={{ height: 22, width: 80, borderRadius: 6 }} />
          </s-stack>
          <div className="shimmer-loader" style={{ height: 8, width: '100%', borderRadius: 4 }} />
          <div className="shimmer-loader" style={{ height: 16, width: '55%', borderRadius: 4 }} />
        </s-stack>
      </s-box>
    );
  }

  if (!balance) return null;

  const { totalCredits = 0, remainingCredits = 0 } = balance;
  const usedCredits = Math.max(0, totalCredits - remainingCredits);
  const isEmpty     = remainingCredits <= 0;
  const isLow       = !isEmpty && totalCredits > 0 && (remainingCredits / totalCredits) <= 0.20;
  
  // Usage percentage (0% = new, 100% = empty)
  const usagePct    = totalCredits > 0 ? Math.min(100, Math.round((usedCredits / totalCredits) * 100)) : 0;

  const badgeTone   = isEmpty ? 'critical' : isLow ? 'attention' : 'success';
  const statusLabel = isEmpty ? '⚠ No credits left' : isLow ? '⚠ Running low' : 'Image credits';
  
  // Colors reflect status of REMAINING credits, but applied to usage bar
  // High usage = red, Low usage = green
  const barColor    = isEmpty ? '#e0211d' : isLow ? '#e6a817' : '#36a420';
  const bgAttr      = isEmpty ? 'critical-subdued' : isLow ? 'warning-subdued' : 'subdued';

  return (
    <s-box padding={compact ? 'small' : 'base'} border="base" borderRadius="base" background={bgAttr}>
      <s-stack gap="small-200">

        {/* Row 1 — status badge + used / total */}
        <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
          <s-badge tone={badgeTone}>{statusLabel}</s-badge>
          <s-text type="strong">
            {usedCredits.toLocaleString()}&nbsp;/&nbsp;{totalCredits.toLocaleString()}
          </s-text>
        </s-stack>

        {/* Progress bar: fill = used credits (0% = full battery, 100% = empty battery) */}
        {/* User requested "from left to right as we use credits" */}
        <div style={{
          height: compact ? 6 : 8,
          background: 'rgba(0,0,0,0.1)',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.05)'
        }}>
          <div style={{
            height: '100%',
            width: `${usagePct}%`,
            background: barColor,
            borderRadius: 4,
            transition: 'width 0.4s ease',
            minWidth: usagePct > 0 ? 4 : 0,
          }} />
        </div>

        {/* Row 3 — descriptive label + action button */}
        <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
          <s-text tone="subdued" size="small">
            {usedCredits.toLocaleString()} used out of {totalCredits.toLocaleString()} purchased
          </s-text>
          {onPurchase && (
            <s-button 
              variant={isEmpty || isLow ? "primary" : "secondary"} 
              onClick={onPurchase}
              size="small"
            >
              {isEmpty ? 'Buy credits' : 'Top up'}
            </s-button>
          )}
        </s-stack>

      </s-stack>
    </s-box>
  );
}

export default CreditsWidget;
